import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../lib/supabaseClient';

const ExamContext = createContext();

export const useExam = () => {
  const context = useContext(ExamContext);
  if (!context) {
    throw new Error('useExam must be used within an ExamProvider');
  }
  return context;
};

// Extend TTL cache from 5 minutes to 30 minutes for better performance
const CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes
const readCache = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (!obj || typeof obj !== 'object') return null;
    if (!obj.ts || Date.now() - obj.ts > CACHE_TTL_MS) return null;
    return obj.data;
  } catch {
    return null;
  }
};
const writeCache = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify({ ts: Date.now(), data }));
  } catch {
    /* ignore */
  }
};

export const ExamProvider = ({ children }) => {
  const { user } = useAuth();
  const [selectedExam, setSelectedExam] = useState(null);
  const [userExams, setUserExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [examsTick, setExamsTick] = useState(0);

  // Load selected exam from localStorage FIRST on mount
  useEffect(() => {
    const savedExam = localStorage.getItem('selectedExam');
    if (savedExam) {
      try {
        const exam = JSON.parse(savedExam);
        // Validate that the exam has required fields and is NOT B2B-only or inactive
        if (
          exam &&
          exam.id &&
          exam.default_settings &&
          (exam.is_b2b_only === false || exam.is_b2b_only == null) &&
          exam.is_active !== false
        ) {
          setSelectedExam(exam);
        } else {
          // Old/invalid/b2b exam data, remove it so fresh valid data will be loaded
          localStorage.removeItem('selectedExam');
        }
      } catch (error) {
        console.error('Error parsing saved exam:', error);
        localStorage.removeItem('selectedExam');
      }
    }
  }, []); // Run only once on mount

  // Load user's selected exams (with cache + fallback to all active exams)
  useEffect(() => {
    const loadUserExams = async () => {
      if (!user) {
        setUserExams([]);
        setLoading(false);
        return;
      }

      const cacheKey = `cache_user_exams_${user.id}`;
      const cached = readCache(cacheKey);
      if (cached) {
        // Strip out any B2B-only or inactive exams from cache
        const validCached = (cached.userExams || []).filter(
          (e) => (e.is_b2b_only === false || e.is_b2b_only == null) && e.is_active !== false
        );
        setUserExams(validCached);
        // Only set selected exam if not already set from localStorage
        if (!selectedExam && (cached.selectedExam || validCached[0])) {
          const examToSet = cached.selectedExam || validCached[0];
          // Validate that the exam is in user's selected exams, not B2B, and has required fields
          if (
            validCached.some((e) => e.id === examToSet.id) &&
            examToSet?.default_settings &&
            (examToSet.is_b2b_only === false || examToSet.is_b2b_only == null) &&
            examToSet.is_active !== false
          ) {
            setSelectedExam(examToSet);
          }
        }
        setLoading(false);
      }

      try {
        // Get user's selected exams from profile
        const { data: profile, error: profileError } = await supabase
          .from('user_profiles')
          .select('selected_exams, updated_at')
          .eq('user_id', user.id)
          .order('updated_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (profileError) throw profileError;

        if (profile?.selected_exams?.length > 0) {
          const { data: exams, error: examsError } = await supabase
            .from('exams')
            .select(
              'id, name, description, default_settings, category_id, study_material_config, is_b2b_only, is_active'
            )
            .in('id', profile.selected_exams)
            .eq('is_active', true)
            .or('is_b2b_only.eq.false,is_b2b_only.is.null');

          if (examsError) throw examsError;

          // Double check filter for non-B2B and active exams
          const validExams = (exams || []).filter(
            (e) => (e.is_b2b_only === false || e.is_b2b_only == null) && e.is_active !== false
          );

          // Sort to match the order the user originally selected them
          const orderedExams = validExams.sort((a, b) => {
            const idxA = profile.selected_exams.indexOf(a.id);
            const idxB = profile.selected_exams.indexOf(b.id);
            return idxA - idxB;
          });
          setUserExams(orderedExams);

          // Update selectedExam with fresh data if it exists and is valid
          let freshExamToSelect = null;
          if (selectedExam) {
            freshExamToSelect = orderedExams.find((e) => e.id === selectedExam.id);
          }
          if (!freshExamToSelect && orderedExams.length > 0) {
            const savedExam = localStorage.getItem('selectedExam');
            if (savedExam) {
              try {
                const parsedExam = JSON.parse(savedExam);
                freshExamToSelect = orderedExams.find((e) => e.id === parsedExam.id);
              } catch {
                freshExamToSelect = null;
              }
            }
            if (!freshExamToSelect) {
              freshExamToSelect = orderedExams[0];
            }
          }

          if (freshExamToSelect) {
            setSelectedExam(freshExamToSelect);
            localStorage.setItem('selectedExam', JSON.stringify(freshExamToSelect));
          } else {
            setSelectedExam(null);
            localStorage.removeItem('selectedExam');
          }

          // Update cache with sanitized exams
          writeCache(cacheKey, {
            selectedExam: freshExamToSelect,
            userExams: orderedExams,
          });
        } else {
          setUserExams([]);
          setSelectedExam(null);
          localStorage.removeItem('selectedExam');
        }
      } catch (error) {
        console.error('Error loading user exams:', error);
        setUserExams([]);
        // Don't clear selectedExam if it's already set from localStorage
        if (!selectedExam) {
          setSelectedExam(null);
        }
      } finally {
        setLoading(false);
      }
    };

    loadUserExams();
  }, [user, examsTick]);

  // Listen for profile exam changes to reload immediately
  useEffect(() => {
    const onChanged = () => setExamsTick((t) => t + 1);
    window.addEventListener('exams-changed', onChanged);
    return () => window.removeEventListener('exams-changed', onChanged);
  }, []);

  // Save selected exam to localStorage for persistence
  useEffect(() => {
    if (selectedExam) {
      localStorage.setItem('selectedExam', JSON.stringify(selectedExam));
    } else {
      localStorage.removeItem('selectedExam');
    }
  }, [selectedExam]);

  // Enhanced setSelectedExam with validation
  const setSelectedExamWithValidation = useCallback(
    (newExam) => {
      // Validate that the exam is in user's selected exams
      const isValidExam = (exam) => {
        return exam && userExams.some((e) => e.id === exam.id);
      };

      // Only validate if this is a user-initiated change (not initial load)
      // Check if user is currently in a test and this is an actual change
      const isTestRoute = /^\/test\//.test(window.location.pathname);
      const isTimed = new URLSearchParams(window.location.search).get('timed') === '1';
      const isActualChange = selectedExam && newExam && selectedExam.id !== newExam.id;

      // Validate the new exam
      if (!isValidExam(newExam)) {
        console.error('Invalid exam selected:', newExam);
        return;
      }

      if (isTestRoute && isActualChange) {
        // For timed tests, exam change is already blocked in Layout
        // For practice tests, we need to handle exam change properly
        if (!isTimed) {
          const ok = window.confirm(
            'Changing exam will end your current test attempt. Are you sure you want to continue?'
          );
          if (!ok) return;

          // Clear test data from localStorage
          localStorage.removeItem('test_answers');
          localStorage.removeItem('test_tita_answers');
          localStorage.removeItem('test_marked_questions');
          localStorage.removeItem('test_current_section');
          localStorage.removeItem('test_current_question');
          localStorage.removeItem('test_time_spent');
          localStorage.removeItem('test_attempt_id');

          // Navigate away from test
          window.location.href = '/dashboard';
          return;
        }
      }

      setSelectedExam(newExam);
    },
    [selectedExam, userExams]
  );

  const value = useMemo(
    () => ({
      selectedExam,
      setSelectedExam: setSelectedExamWithValidation,
      userExams,
      loading,
    }),
    [selectedExam, setSelectedExamWithValidation, userExams, loading]
  );

  return <ExamContext.Provider value={value}>{children}</ExamContext.Provider>;
};

export default ExamContext;
